import React, { Fragment } from "react";

import clsx from "clsx";

import { ControlBar } from "@components/control-bar";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { ExpandableBreakdownSelector } from "@components/reports/breakdown-selector";
import { StuckToTop } from "@components/stuck-to-top";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  CompareDateRangeEnum,
  DateRangeChoiceEnum,
  TimeGranularity_Deprecated,
} from "@north-beam/nb-common";
import { formatDateRangeText } from "@shared/date-range-picker";
import {
  AttributionModelSelect,
  CompareDateRangeSelect,
  DateRangeSelect,
  TimeGranularitySelect,
} from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

import SavedViews from "../saved-views";
import { TabView } from "../tab-table-section/tab-table-section";

import { AttributionWindowSelect } from "./attribution-window-select";
import { NbAccountingModeSelect } from "./nb-accounting-mode-select";
import {
  type ReportBodyState,
  useReportBodyState,
} from "./use-report-body-state";

export interface ReportBodyControlProps {
  isLoading: boolean;
  setObjectIdsToAdLevelFilters: (
    selectedRows: string[],
    value: TabView,
  ) => void;
}

export const ReportBodyControl = ({
  isLoading,
  setObjectIdsToAdLevelFilters,
}: ReportBodyControlProps) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const {
    state,

    attributionModel,
    setAttributionModel,

    attributionWindow,
    setAttributionWindow,

    nbAccountingMode,
    setNbAccountingMode,

    columnSet,
    setColumnSet,

    granularity,
    setGranularity,

    breakdowns,
    setBreakdowns,

    timeGranularity,
    setTimeGranularity,

    setDateRange,
    setCompareDateRange,
  } = useReportBodyState();

  let maxLookbackDays = 20 * 365;
  let maxLookbackDaysCompare = 20 * 365;

  if (timeGranularity === "hourly") {
    maxLookbackDays = 8;
    maxLookbackDaysCompare = 15;
  }

  const dateRangeCurrent =
    typeof state.dateRange === "string"
      ? DateRangeChoiceEnum.toReactSelect(state.dateRange)
      : state.dateRange;

  const compareDateRangeCurrent =
    typeof state.compareDateRange === "string"
      ? CompareDateRangeEnum.toReactSelect(state.compareDateRange)
      : state.compareDateRange;

  const isAttributionWindowVisible =
    ((columnSet === "overview" || columnSet === "first_time_vs_returning") &&
      nbAccountingMode === "accrual") ||
    nbAccountingMode === "accrual";

  return (
    <StuckToTop>
      {() => (
        <>
          <SavedViews />
          <ControlBar>
            <div
              className="flex space-x-2 max-w-7xl"
              style={{ flex: "3 3 auto" }}
            >
              <AttributionModelSelect
                isLoading={isLoading}
                attributionModel={attributionModel}
                onChange={setAttributionModel}
              />
              {isAttributionWindowVisible && (
                <AttributionWindowSelect
                  isLoading={isLoading}
                  attributionModel={attributionModel}
                  attributionWindow={attributionWindow}
                  timeGranularity={timeGranularity}
                  onChange={setAttributionWindow}
                />
              )}
              <NbAccountingModeSelect
                isLoading={isLoading}
                nbAccountingMode={nbAccountingMode}
                onChange={setNbAccountingMode}
              />
              <ExpandableBreakdownSelector
                isLoading={isLoading}
                current={breakdowns}
                available={breakdownConfigs}
                allConfigs={breakdownConfigs}
                onUpdate={(arg) => {
                  setBreakdowns(arg);
                  if (granularity === TabView.PLATFORM) {
                    setGranularity(TabView.CAMPAIGN);
                    setObjectIdsToAdLevelFilters([], TabView.CAMPAIGN);
                  }
                }}
              />
            </div>
            <div className="flex flex-row items-center justify-content-end pl-5">
              <Popover className="nb-input-container relative">
                <PopoverButton
                  className={clsx(
                    "nb-input flex flex-col items-start mr-2",
                    "text-sm min-w-[14rem] w-max h-14",
                  )}
                  disabled={isLoading}
                >
                  {({ open }) => (
                    <div className="flex items-center space-between w-full">
                      <div className="flex-grow text-left mr-4">
                        <div>
                          {formatDateRangeText(dateRangeCurrent)},{" "}
                          {TimeGranularity_Deprecated.getLabel(timeGranularity)}
                        </div>
                        <small className="text-muted mt-1 text-small">
                          Compared to{" "}
                          {formatDateRangeText(compareDateRangeCurrent)}
                        </small>
                      </div>
                      {renderChevronAnimation(open)}
                    </div>
                  )}
                </PopoverButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <PopoverPanel
                    className={clsx(
                      "absolute right-0 z-100 mt-2 w-72 elevation-1 rounded-md",
                      "p-3 bg-white flex flex-col space-y-2",
                    )}
                  >
                    <TimeGranularitySelect
                      isLoading={isLoading}
                      timeGranularity={timeGranularity}
                      onChange={setTimeGranularity}
                    />
                    <DateRangeSelect
                      isLoading={isLoading}
                      maxLookbackDays={maxLookbackDays}
                      dateRange={state.dateRange}
                      onUpdate={setDateRange}
                    />
                    <CompareDateRangeSelect
                      isLoading={isLoading}
                      dateRange={state.dateRange}
                      compareDateRange={state.compareDateRange}
                      onUpdate={setCompareDateRange}
                      maxLookbackDays={maxLookbackDaysCompare}
                    />
                  </PopoverPanel>
                </Transition>
              </Popover>
            </div>
          </ControlBar>
        </>
      )}
    </StuckToTop>
  );
};

export function makeReportBodyStateQuery(
  state: ReportBodyState,
): URLSearchParams {
  const search = new URLSearchParams();
  search.set("attributionModel", state.attributionModel);
  search.set("attributionWindow", state.attributionWindow);
  search.set("nbAccountingMode", state.nbAccountingMode);
  search.set("columnSet", state.columnSet);
  search.set("breakdowns", JSON.stringify(state.breakdowns));
  search.set("granularity", state.granularity);
  search.set("timeGranularity", state.timeGranularity);
  search.set("dateRange", JSON.stringify(state.dateRange));
  search.set("compareDateRange", JSON.stringify(state.compareDateRange));
  return search;
}
